import { cn } from "@/lib/utils";
import React, { forwardRef } from "react";
import { Button as UIButton, buttonVariants } from "@/components/ui/button";
import { Link } from "react-router-dom";

type ButtonProps = {
  children?: React.ReactNode;
  className?: string;
  variant?: "simple" | "outline" | "primary" | "default" | "ghost" | "secondary";
  as?: React.ElementType;
  href?: string;
  to?: string;
  asChild?: boolean;
  [x: string]: any;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  className,
  variant = "primary",
  as: Tag,
  asChild = false,
  href,
  to,
  ...props
}, ref) => {
  // If it uses asChild, delegate to the UI Button
  if (asChild) {
    // Map our variants to UI button variants
    let mappedVariant: any = variant;
    if (variant === "simple") mappedVariant = "outline";
    if (variant === "primary") mappedVariant = "default";
    
    return (
      <UIButton
        variant={mappedVariant}
        className={className}
        asChild={asChild}
        ref={ref}
        {...props}
      >
        {children}
      </UIButton>
    );
  }
  
  // Handle links (both external href and internal to)
  if (href || to) {
    // For internal navigation links (to), use a direct approach that forces scroll reset
    if (to) {
      return (
        <a
          href={to} // Use href instead of React Router Link
          className={cn(
            "relative z-10 text-sm font-medium flex items-center justify-center",
            getVariantClass(variant),
            className
          )}
          onClick={(e) => {
            // Let default browser navigation handle this instead of React Router
            // This will fully reset the page scroll state
            if (props.onClick) {
              props.onClick(e);
            }
          }}
          {...props}
        >
          {children ?? `Get Started`}
        </a>
      );
    }
    
    // For external links, use the standard approach
    return (
      <a
        href={href}
        className={cn(
          "relative z-10 text-sm font-medium flex items-center justify-center",
          getVariantClass(variant),
          className
        )}
        {...props}
      >
        {children ?? `Get Started`}
      </a>
    );
  }
  
  // Otherwise use our custom button styles
  const Comp = Tag || "button";
  
  return (
    <Comp
      className={cn(
        "relative z-10 text-sm font-medium flex items-center justify-center",
        getVariantClass(variant),
        className
      )}
      ref={ref}
      {...props}
    >
      {children ?? `Get Started`}
    </Comp>
  );
});

function getVariantClass(variant: string) {
  switch (variant) {
    case "simple":
      return "bg-transparent hover:bg-slate-100 border border-transparent text-foreground transition rounded-full px-4 py-2 dark:hover:bg-neutral-800";
    case "outline":
      return "bg-background hover:bg-primary/10 text-foreground border border-input hover:text-foreground transition rounded-full px-4 py-2";
    case "primary":
      return "bg-primary hover:bg-primary/90 border border-transparent text-white transition rounded-full px-4 py-2";
    default:
      return "bg-primary hover:bg-primary/90 text-primary-foreground transition rounded-full px-4 py-2";
  }
}

Button.displayName = "Button";